import type {
  ConnectionPoint,
  Point,
  ShapeStyle,
  ShapeType,
  Size,
} from '../state/types';
import { buildEdgeMidpoints } from './connectionPoints';
import { Shape } from './Shape';

export class Cylinder extends Shape {
  readonly type: ShapeType = 'cylinder';
  private readonly ellipseRatio = 0.2; // Height of ellipse as ratio of width

  constructor(
    position: Point,
    size: Size,
    style?: Partial<ShapeStyle>,
    text = ''
  ) {
    super(position, size, style, text);
  }

  containsPoint(p: Point): boolean {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const ellipseHeight = width * this.ellipseRatio;

    // Check if in main rectangle body
    if (
      p.x >= x &&
      p.x <= x + width &&
      p.y >= y + ellipseHeight / 2 &&
      p.y <= y + height - ellipseHeight / 2
    ) {
      return true;
    }

    // Check top ellipse
    const topCenter = { x: x + width / 2, y: y + ellipseHeight / 2 };
    const rx = width / 2;
    const ry = ellipseHeight / 2;
    const dxTop = (p.x - topCenter.x) / rx;
    const dyTop = (p.y - topCenter.y) / ry;
    if (dxTop * dxTop + dyTop * dyTop <= 1) return true;

    // Check bottom ellipse
    const bottomCenter = {
      x: x + width / 2,
      y: y + height - ellipseHeight / 2,
    };
    const dxBottom = (p.x - bottomCenter.x) / rx;
    const dyBottom = (p.y - bottomCenter.y) / ry;
    if (dxBottom * dxBottom + dyBottom * dyBottom <= 1) return true;

    return false;
  }

  getConnectionPoints(): ConnectionPoint[] {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const ellipseHeight = width * this.ellipseRatio;
    const edgeMidpoints = buildEdgeMidpoints(this.id, this.position, this.size);

    return [
      ...edgeMidpoints,
      // Top ellipse edges (left and right)
      {
        id: `${this.id}-top-left`,
        position: { x, y: y + ellipseHeight / 2 },
        direction: 'left',
      },
      {
        id: `${this.id}-top-right`,
        position: { x: x + width, y: y + ellipseHeight / 2 },
        direction: 'right',
      },
      // Bottom ellipse edges (left and right)
      {
        id: `${this.id}-bottom-left`,
        position: { x, y: y + height - ellipseHeight / 2 },
        direction: 'left',
      },
      {
        id: `${this.id}-bottom-right`,
        position: { x: x + width, y: y + height - ellipseHeight / 2 },
        direction: 'right',
      },
    ];
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { x, y } = this.position;
    const { width, height } = this.size;
    const ellipseHeight = width * this.ellipseRatio;
    const rx = width / 2;
    const ry = ellipseHeight / 2;
    const centerX = x + width / 2;

    ctx.save();
    ctx.globalAlpha = this.style.opacity;

    // Draw body (sides)
    ctx.beginPath();
    ctx.moveTo(x, y + ry);
    ctx.lineTo(x, y + height - ry);
    ctx.ellipse(
      centerX,
      y + height - ry,
      rx,
      ry,
      0,
      Math.PI,
      0,
      true
    );
    ctx.lineTo(x + width, y + ry);
    ctx.ellipse(centerX, y + ry, rx, ry, 0, 0, Math.PI, true);
    ctx.closePath();

    ctx.fillStyle = this.style.fill;
    ctx.fill();

    ctx.strokeStyle = this.style.stroke;
    ctx.lineWidth = this.style.strokeWidth;
    ctx.stroke();

    // Draw top ellipse (on top of fill for proper layering)
    ctx.beginPath();
    ctx.ellipse(centerX, y + ry, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = this.style.fill;
    ctx.fill();
    ctx.strokeStyle = this.style.stroke;
    ctx.stroke();

    ctx.restore();

    this.renderText(ctx);
    this.renderSelection(ctx);
  }
}
